import React, { useMemo, useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../../../../../components/ui/tabs';
import { Badge } from '../../../../../../../components/ui/badge';
import { Card, CardContent } from '../../../../../../../components/ui/card';

interface WeldParametersTableProps {
    jobData: any;
}

interface PassData {
    Type: string;
    Torch: string;
    Pass: string;
    PassName: string;
    PassPendName: string;
    PassGroupNumber: string;
    EndingAngle: string;
    PassEnable: string;
    CommonWeld?: any;
    MotorJog?: any;
    WireFeed?: any;
    Travel?: any;
    Oscillation?: any;
    PowerSupplyController?: any;
    PowerSupplyPulseArc?: any;
    PowerSupplyShortArc?: any;
    ArcStartup?: any;
    ArcTermination?: any;
    VerticalTracking?: any;
    CrossTracking?: any;
}

interface GroupedPass {
    passNumber: string;
    lead?: PassData;
    trail?: PassData;
}

const WeldParametersTable: React.FC<WeldParametersTableProps> = ({ jobData }) => {
    const [activeTab, setActiveTab] = useState<'lead' | 'trail' | 'all'>('lead');

    // Extract and group pass data
    const { groupedPasses, categories } = useMemo(() => {
        if (!jobData?.parsed_json_data) {
            return { groupedPasses: [], categories: [] };
        }

        const passData = jobData.parsed_json_data.filter((item: any) => item.Type === 'PASS') as PassData[];

        // Group passes by pass number
        const groups: { [key: string]: { lead?: PassData; trail?: PassData } } = {};

        passData.forEach((pass) => {
            const passNumber = pass.Pass;
            if (!groups[passNumber]) {
                groups[passNumber] = {};
            }

            if (pass.Torch === 'LEAD') {
                groups[passNumber].lead = pass;
            } else if (pass.Torch === 'TRAIL') {
                groups[passNumber].trail = pass;
            }
        });

        const groupedArray: GroupedPass[] = Object.keys(groups)
            .sort((a, b) => {
                const numA = parseInt(a.replace('PASS ', ''));
                const numB = parseInt(b.replace('PASS ', ''));
                return numA - numB;
            })
            .map(passNumber => ({
                passNumber,
                lead: groups[passNumber].lead,
                trail: groups[passNumber].trail
            }));

        // Define parameter categories
        const paramCategories = [
            {
                name: 'COMMON WELD',
                key: 'CommonWeld',
                parameters: [
                    { key: 'PipeDiameter', label: 'Pipe Diameter' },
                    { key: 'BandDiameter', label: 'Band Diameter' },
                    { key: 'WireDiameter', label: 'Wire Diameter' },
                    { key: 'PowerSupply', label: 'Power Supply' },
                    { key: 'WeldingProcess', label: 'Welding Process' },
                    { key: 'HeatInputHighLimit', label: 'Heat Input High Limit' },
                    { key: 'HeatInputLowLimit', label: 'Heat Input Low Limit' },
                ]
            },
            {
                name: 'Wire Feed',
                key: 'WireFeed',
                parameters: [
                    { key: 'WireFeedSpeed', label: 'Wire Feed Speed' },
                    { key: 'JogStartWireFeedSpeed', label: 'Jog Start Wire Feed Speed' },
                    { key: 'WireFeedSpeedRampUpTime', label: 'Wire Feed Speed Ramp Up Time' },
                    { key: 'WireFeedSpeedRampDownTime', label: 'Wire Feed Speed Ramp Down Time' },
                    { key: 'WireFeedSpeedHighLimit', label: 'Wire Feed Speed High Limit' },
                    { key: 'WireFeedSpeedLowLimit', label: 'Wire Feed Speed Low Limit' },
                ]
            },
            {
                name: 'Travel',
                key: 'Travel',
                parameters: [
                    { key: 'TravelSpeed', label: 'Travel Speed' },
                    { key: 'TravelSpeedHighLimit', label: 'Travel Speed High Limit' },
                    { key: 'TravelSpeedLowLimit', label: 'Travel Speed Low Limit' },
                    { key: 'AutoStartAndStop', label: 'Auto Start And Stop' },
                    { key: 'AutoStartDistance', label: 'Auto Start Distance' },
                    { key: 'AutoStopDistance', label: 'Auto Stop Distance' },
                    { key: 'PotFunction', label: 'Pot Function' },
                ]
            },
            {
                name: 'Oscillation',
                key: 'Oscillation',
                parameters: [
                    { key: 'OscFreq', label: 'Osc Freq' },
                    { key: 'OscWidth', label: 'Osc Width' },
                    { key: 'LeftDwell', label: 'Left Dwell' },
                    { key: 'RightDwell', label: 'Right Dwell' },
                    { key: 'OscWidthHighLimit', label: 'Osc Width High Limit' },
                    { key: 'OscWidthLowLimit', label: 'Osc Width Low Limit' },
                    { key: 'CapSplitDistanceRight', label: 'Cap Split Distance Right' },
                    { key: 'GasValve', label: 'Gas Valve' },
                ]
            },
            {
                name: 'Power Supply Controller',
                key: 'PowerSupplyController',
                parameters: [
                    { key: 'CableResistance', label: 'Cable Resistance' },
                    { key: 'DynamicCorrection', label: 'Dynamic Correction' },
                    { key: 'MasterSlaveSel', label: 'Master Slave Sel' },
                    { key: 'ProgramNumber', label: 'Program Number' },
                ]
            },
            {
                name: 'Power Supply Pulse Arc',
                key: 'PowerSupplyPulseArc',
                parameters: [
                    { key: 'Trim', label: 'Trim' },
                    { key: 'TrimHighLimit', label: 'Trim High Limit' },
                    { key: 'TrimLowLimit', label: 'Trim Low Limit' },
                    { key: 'TrimRangeHigh', label: 'Trim Range High' },
                    { key: 'TrimRangeLow', label: 'Trim Range Low' },
                    { key: 'WorkPointHighLimit', label: 'Work Point High Limit' },
                    { key: 'WorkPointLowLimit', label: 'Work Point Low Limit' },
                    { key: 'WorkPointRangeHigh', label: 'Work Point Range High' },
                    { key: 'WorkPointRangeLow', label: 'Work Point Range Low' },
                ]
            },
            {
                name: 'Power Supply Short Arc',
                key: 'PowerSupplyShortArc',
                parameters: [
                    { key: 'ArcVoltage', label: 'Arc Voltage' },
                    { key: 'ArcVoltageHighLimit', label: 'Arc Voltage High Limit' },
                    { key: 'ArcVoltageLowLimit', label: 'Arc Voltage Low Limit' },
                    { key: 'ArcVoltageRangeHigh', label: 'Arc Voltage Range High' },
                    { key: 'ArcVoltageRangeLow', label: 'Arc Voltage Range Low' },
                    { key: 'PowerSupplyRegulatedBy', label: 'Power Supply Regulated By' },
                ]
            },
            {
                name: 'Arc Startup',
                key: 'ArcStartup',
                parameters: [
                    { key: 'HotStartWorkPoint', label: 'Hot Start Work Point' },
                    { key: 'HotStartVoltage', label: 'Hot Start Voltage' },
                    { key: 'HotStartPeriod', label: 'Hot Start Period' },
                ]
            },
            {
                name: 'Arc Termination',
                key: 'ArcTermination',
                parameters: [
                    { key: 'WorkPointWeldStopRampTime', label: 'Work Point Weld Stop Ramp Time' },
                    { key: 'WorkPointEndingValue', label: 'Work Point Ending Value' },
                    { key: 'CraterFillTime', label: 'Crater Fill Time' },
                    { key: 'BurnBackTime', label: 'Burn Back Time' },
                    { key: 'PullBack', label: 'Pull Back' },
                ]
            },
            {
                name: 'Vertical Tracking',
                key: 'VerticalTracking',
                parameters: [
                    { key: 'VerticalTracking', label: 'Vertical Tracking' },
                    { key: 'VerticalTarget', label: 'Vertical Target' },
                    { key: 'VerticalTargetHighLimit', label: 'Vertical Target High Limit' },
                    { key: 'VerticalTargetLowLimit', label: 'Vertical Target Low Limit' },
                    { key: 'TrackingThresholdAmps', label: 'Tracking Threshold Amps' },
                    { key: 'TrackingThresholdVolts', label: 'Tracking Threshold Volts' },
                    { key: 'VerticalTrackingSpeed', label: 'Vertical Tracking Speed' },
                    { key: 'VerticalDivisionFactor', label: 'Vertical Division Factor' },
                ]
            },
            {
                name: 'Cross Tracking',
                key: 'CrossTracking',
                parameters: [
                    { key: 'CrossTrackingEnable', label: 'Cross Tracking Enable' },
                    { key: 'CrossSeamBiasResetValue', label: 'Cross Seam Bias Reset Value' },
                    { key: 'CrossSeamBiasHighLimit', label: 'Cross Seam Bias High Limit' },
                    { key: 'CrossSeamBiasLowLimit', label: 'Cross Seam Bias Low Limit' },
                    { key: 'CrossDivisionFactor', label: 'Cross Division Factor' },
                ]
            },
        ];

        return { groupedPasses: groupedArray, categories: paramCategories };
    }, [jobData]);

    // Filter passes based on active tab
    const filteredPasses = useMemo(() => {
        switch (activeTab) {
            case 'lead':
                return groupedPasses.filter(p => p.lead);
            case 'trail':
                return groupedPasses.filter(p => p.trail);
            default:
                return groupedPasses;
        }
    }, [groupedPasses, activeTab]);

    // Get status badge for Pass Enable
    const getStatusBadge = (status: string | undefined) => {
        if (!status) return <Badge variant="secondary">N/A</Badge>;

        if (status.toLowerCase() === 'yes') {
            return <Badge className="bg-green-600 hover:bg-green-700">Yes</Badge>;
        } else if (status.toLowerCase() === 'no') {
            return <Badge variant="destructive">No</Badge>;
        }
        return <Badge variant="secondary">{status}</Badge>;
    };

    const renderTableContent = () => {
        if (!filteredPasses.length) {
            return (
                <div className="text-center py-12 text-gray-500">
                    <p className="text-lg font-medium text-gray-700">No weld parameters data available</p>
                </div>
            );
        }

        // Calculate total columns for category headers
        const totalColumns = activeTab === 'all'
            ? filteredPasses.reduce((sum, g) => sum + (g.lead ? 1 : 0) + (g.trail ? 1 : 0), 0)
            : filteredPasses.length;

        return (
            <>
                <style>{`
                    .p600z-weld-scroll-container::-webkit-scrollbar {
                        width: 12px;
                        height: 12px;
                    }
                    .p600z-weld-scroll-container::-webkit-scrollbar-track {
                        background: #f3f4f6;
                        border-radius: 6px;
                    }
                    .p600z-weld-scroll-container::-webkit-scrollbar-thumb {
                        background: #9ca3af;
                        border-radius: 6px;
                        border: 2px solid #f3f4f6;
                    }
                    .p600z-weld-scroll-container::-webkit-scrollbar-thumb:hover {
                        background: #6b7280;
                    }
                    .p600z-weld-scroll-container::-webkit-scrollbar-corner {
                        background: #f3f4f6;
                    }
                `}</style>
                <div className="w-full flex flex-col min-h-0">
                <Card className="bg-white border border-gray-200 flex flex-col min-h-0">
                    <CardContent className="p-0 w-full flex flex-col min-h-0">
                    <div className="overflow-x-auto overflow-y-auto min-h-0 p600z-weld-scroll-container" style={{ 
                        scrollbarWidth: 'thin',
                        scrollbarColor: '#9ca3af #f3f4f6',
                        WebkitOverflowScrolling: 'touch',
                        overscrollBehaviorX: 'contain',
                        maxHeight: 'calc(100vh - 220px)'
                    }}>
                    <table className="w-full caption-bottom text-sm" style={{ borderCollapse: 'separate', borderSpacing: 0, minWidth: 'max-content' }}>
                    <thead className="sticky top-0 bg-white z-20" style={{ boxShadow: '0 2px 0 0 rgba(209, 213, 219, 1)' }}>
                        <tr className="bg-gray-100">
                            <th className="sticky left-0 z-30 bg-gray-100 border-r-2 border-gray-300 border-b border-gray-300 min-w-[250px] font-bold h-12 px-4 align-middle">
                               
                            </th>
                            {filteredPasses.map((group) => (
                                <React.Fragment key={group.passNumber}>
                                    {activeTab === 'all' && (
                                        <>
                                            {group.lead && (
                                                <th className="text-center border-r border-gray-300 border-b border-gray-300 min-w-[120px] font-bold bg-blue-50 h-12 px-4 align-middle">
                                                    {group.passNumber}
                                                </th>
                                            )}
                                            {group.trail && (
                                                <th className="text-center border-r border-gray-300 border-b border-gray-300 min-w-[120px] font-bold bg-orange-50 h-12 px-4 align-middle">
                                                    {group.passNumber}
                                                </th>
                                            )}
                                        </>
                                    )}
                                    {activeTab === 'lead' && group.lead && (
                                        <th className="text-center border-r border-gray-300 border-b border-gray-300 min-w-[120px] font-bold bg-blue-50 h-12 px-4 align-middle">
                                            {group.passNumber}
                                        </th>
                                    )}
                                    {activeTab === 'trail' && group.trail && (
                                        <th className="text-center border-r border-gray-300 border-b border-gray-300 min-w-[120px] font-bold bg-orange-50 h-12 px-4 align-middle">
                                            {group.passNumber}
                                        </th>
                                    )}
                                </React.Fragment>
                            ))}
                        </tr>

                        <tr className="bg-gray-100">
                            <th className="sticky left-0 z-30 bg-gray-100 border-r-2 border-gray-300 border-b-2 border-gray-300 min-w-[200px] font-bold h-12 px-4 align-middle">
                                Pass Name
                            </th>
                            {filteredPasses.map((group) => (
                                <React.Fragment key={`pass-name-${group.passNumber}`}>
                                    {activeTab === 'all' && (
                                        <>
                                            {group.lead && (
                                                <th className="text-center border-r border-gray-300 border-b-2 border-gray-300 min-w-[120px] font-bold bg-blue-50 h-12 px-4 align-middle">
                                                    {group.lead.PassName|| '-'}
                                                </th>
                                            )}
                                            {group.trail && (
                                                <th className="text-center border-r border-gray-300 border-b-2 border-gray-300 min-w-[120px] font-bold bg-orange-50 h-12 px-4 align-middle">
                                                    {group.trail.PassName || '-'}
                                                </th>
                                            )}
                                        </>
                                    )}
                                    {activeTab === 'lead' && group.lead && (
                                        <th className="text-center border-r border-gray-300 border-b-2 border-gray-300 min-w-[120px] font-bold bg-blue-50 h-12 px-4 align-middle">
                                            {group.lead.PassName  || '-'}
                                        </th>
                                    )}
                                    {activeTab === 'trail' && group.trail && (
                                        <th className="text-center border-r border-gray-300 border-b-2 border-gray-300 min-w-[120px] font-bold bg-orange-50 h-12 px-4 align-middle">
                                            {group.trail.PassName || '-'}
                                        </th>
                                    )}
                                </React.Fragment>
                            ))}
                        </tr>
                    </thead>

                    <tbody className="[&_tr:last-child]:border-0">
                        {/* Pass pendant name Row */}
                        <tr className="bg-gray-50 transition-colors hover:bg-gray-50/50">
                            <td className="px-4 h-12 text-sm font-semibold text-gray-800 bg-gray-50 sticky left-0 z-10 border-r-2 border-gray-300 border-b border-gray-200 align-middle">
                                Pass pendant name
                            </td>
                            {filteredPasses.map((group) => (
                                <React.Fragment key={`names-${group.passNumber}`}>
                                    {activeTab === 'all' && (
                                        <>
                                            {group.lead && (
                                                <td className="px-4 h-12 text-sm text-center border-r border-gray-300 font-medium bg-blue-50 border-b border-gray-200 align-middle">
                                                    {group.lead.PassPendName || group.lead.PassName || '-'}
                                                </td>
                                            )}
                                            {group.trail && (
                                                <td className="px-4 h-12 text-sm text-center border-r border-gray-300 font-medium bg-orange-50 border-b border-gray-200 align-middle">
                                                    {group.trail.PassPendName || group.trail.PassName || '-'}
                                                </td>
                                            )}
                                        </>
                                    )}
                                    {activeTab === 'lead' && group.lead && (
                                        <td className="px-4 h-12 text-sm text-center border-r border-gray-300 font-medium bg-blue-50 border-b border-gray-200 align-middle">
                                            {group.lead.PassPendName || group.lead.PassName || '-'}
                                        </td>
                                    )}
                                    {activeTab === 'trail' && group.trail && (
                                        <td className="px-4 h-12 text-sm text-center border-r border-gray-300 font-medium bg-orange-50 border-b border-gray-200 align-middle">
                                            {group.trail.PassPendName || group.trail.PassName || '-'}
                                        </td>
                                    )}
                                </React.Fragment>
                            ))}
                        </tr>

                        {/* Pass Group Number Row */}
                        <tr className="bg-gray-50 transition-colors hover:bg-gray-50/50">
                            <td className="px-4 h-12 text-sm font-semibold text-gray-800 bg-gray-50 sticky left-0 z-10 border-r-2 border-gray-300 border-b border-gray-200 align-middle">
                                Pass Group Number
                            </td>
                            {filteredPasses.map((group) => (
                                <React.Fragment key={`group-${group.passNumber}`}>
                                    {activeTab === 'all' && (
                                        <>
                                            {group.lead && (
                                                <td className="px-4 h-12 text-sm text-center border-r border-gray-300 font-mono bg-blue-50 border-b border-gray-200 align-middle">
                                                    {group.lead.PassGroupNumber || '-'}
                                                </td>
                                            )}
                                            {group.trail && (
                                                <td className="px-4 h-12 text-sm text-center border-r border-gray-300 font-mono bg-orange-50 border-b border-gray-200 align-middle">
                                                    {group.trail.PassGroupNumber || '-'}
                                                </td>
                                            )}
                                        </>
                                    )}
                                    {activeTab === 'lead' && group.lead && (
                                        <td className="px-4 h-12 text-sm text-center border-r border-gray-300 font-mono bg-blue-50 border-b border-gray-200 align-middle">
                                            {group.lead.PassGroupNumber || '-'}
                                        </td>
                                    )}
                                    {activeTab === 'trail' && group.trail && (
                                        <td className="px-4 h-12 text-sm text-center border-r border-gray-300 font-mono bg-orange-50 border-b border-gray-200 align-middle">
                                            {group.trail.PassGroupNumber || '-'}
                                        </td>
                                    )}
                                </React.Fragment>
                            ))}
                        </tr>

                        {/* Ending Angle Row */}
                        <tr className="bg-gray-50 transition-colors hover:bg-gray-50/50">
                            <td className="px-4 h-12 text-sm font-semibold text-gray-800 bg-gray-50 sticky left-0 z-10 border-r-2 border-gray-300 border-b border-gray-200 align-middle">
                                Ending Angle
                            </td>
                            {filteredPasses.map((group) => (
                                <React.Fragment key={`angle-${group.passNumber}`}>
                                    {activeTab === 'all' && (
                                        <>
                                            {group.lead && (
                                                <td className="px-4 h-12 text-sm text-center border-r border-gray-300 font-mono bg-blue-50 border-b border-gray-200 align-middle">
                                                    {group.lead.EndingAngle || '-'}
                                                </td>
                                            )}
                                            {group.trail && (
                                                <td className="px-4 h-12 text-sm text-center border-r border-gray-300 font-mono bg-orange-50 border-b border-gray-200 align-middle">
                                                    {group.trail.EndingAngle || '-'}
                                                </td>
                                            )}
                                        </>
                                    )}
                                    {activeTab === 'lead' && group.lead && (
                                        <td className="px-4 h-12 text-sm text-center border-r border-gray-300 font-mono bg-blue-50 border-b border-gray-200 align-middle">
                                            {group.lead.EndingAngle || '-'}
                                        </td>
                                    )}
                                    {activeTab === 'trail' && group.trail && (
                                        <td className="px-4 h-12 text-sm text-center border-r border-gray-300 font-mono bg-orange-50 border-b border-gray-200 align-middle">
                                            {group.trail.EndingAngle || '-'}
                                        </td>
                                    )}
                                </React.Fragment>
                            ))}
                        </tr>

                        {/* Pass Enable Row */}
                        <tr className="bg-gray-50 transition-colors hover:bg-gray-50/50">
                            <td className="px-4 h-12 text-sm font-semibold text-gray-800 bg-gray-50 sticky left-0 z-10 border-r-2 border-gray-300 border-b border-gray-200 align-middle">
                                Pass Enable
                            </td>
                            {filteredPasses.map((group) => (
                                <React.Fragment key={`enable-${group.passNumber}`}>
                                    {activeTab === 'all' && (
                                        <>
                                            {group.lead && (
                                                <td className="px-4 h-12 text-sm text-center border-r border-gray-300 bg-blue-50 border-b border-gray-200 align-middle">
                                                    {getStatusBadge(group.lead.PassEnable)}
                                                </td>
                                            )}
                                            {group.trail && (
                                                <td className="px-4 h-12 text-sm text-center border-r border-gray-300 bg-orange-50 border-b border-gray-200 align-middle">
                                                    {getStatusBadge(group.trail.PassEnable)}
                                                </td>
                                            )}
                                        </>
                                    )}
                                    {activeTab === 'lead' && group.lead && (
                                        <td className="px-4 h-12 text-sm text-center border-r border-gray-300 bg-blue-50 border-b border-gray-200 align-middle">
                                            {getStatusBadge(group.lead.PassEnable)}
                                        </td>
                                    )}
                                    {activeTab === 'trail' && group.trail && (
                                        <td className="px-4 h-12 text-sm text-center border-r border-gray-300 bg-orange-50 border-b border-gray-200 align-middle">
                                            {getStatusBadge(group.trail.PassEnable)}
                                        </td>
                                    )}
                                </React.Fragment>
                            ))}
                        </tr>
                        {categories.map((category) => (
                            <React.Fragment key={category.key}>
                                {/* Category Header Row */}
                                <tr className="bg-gray-200 transition-colors hover:bg-gray-50/50">
                                    {/* First column - Category name, sticky left */}
                                    <td 
                                        className="px-3 py-2 text-sm font-bold text-gray-800 bg-gray-200 sticky left-0 z-10 border-r-2 border-gray-300 border-b-2 border-gray-300 align-middle"
                                        style={{ top: '96px' }}
                                    >
                                        {category.name}
                                    </td>
                                    {/* Remaining columns - one for each pass */}
                                    {filteredPasses.map((group) => (
                                        <React.Fragment key={`category-${group.passNumber}`}>
                                            {activeTab === 'all' && (
                                                <>
                                                    {group.lead && (
                                                        <td className="px-2 py-2 text-sm font-bold text-gray-800 bg-gray-200 align-middle">
                                                            {/* Empty cell with same styling */}
                                                        </td>
                                                    )}
                                                    {group.trail && (
                                                        <td className="px-2 py-2 text-sm font-bold text-gray-800 bg-gray-200 align-middle">
                                                            {/* Empty cell with same styling */}
                                                        </td>
                                                    )}
                                                </>
                                            )}
                                            {activeTab === 'lead' && group.lead && (
                                                <td className="px-2 py-2 text-sm font-bold text-gray-800 bg-gray-200 align-middle">
                                                    {/* Empty cell with same styling */}
                                                </td>
                                            )}
                                            {activeTab === 'trail' && group.trail && (
                                                <td className="px-2 py-2 text-sm font-bold text-gray-800 bg-gray-200 align-middle">
                                                    {/* Empty cell with same styling */}
                                                </td>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tr>

                                {/* Parameter Rows */}
                                {category.parameters.map((param) => (
                                    <tr key={`${category.key}-${param.key}`} className="transition-colors hover:bg-gray-50/50">
                                        <td className="w-[200px] min-w-[200px] max-w-[200px] bg-white px-4 h-12 text-sm font-medium text-gray-700 sticky left-0 z-10 border-r-2 border-gray-300 border-b border-gray-200 align-middle">
                                            {param.label}
                                        </td>
                                        {filteredPasses.map((group) => (
                                            <React.Fragment key={`${group.passNumber}-${param.key}`}>
                                                {activeTab === 'all' && (
                                                    <>
                                                        {group.lead && (
                                                            <td className="px-4 h-12 text-sm text-center border-r border-gray-300 font-mono border-b border-gray-200 align-middle">
                                                                {(group.lead as any)[category.key]?.[param.key] || '-'}
                                                            </td>
                                                        )}
                                                        {group.trail && (
                                                            <td className="px-4 h-12 text-sm text-center border-r border-gray-300 font-mono border-b border-gray-200 align-middle">
                                                                {(group.trail as any)[category.key]?.[param.key] || '-'}
                                                            </td>
                                                        )}
                                                    </>
                                                )}
                                                {activeTab === 'lead' && group.lead && (
                                                    <td className="px-4 h-12 text-sm text-center border-r border-gray-300 font-mono border-b border-gray-200 align-middle">
                                                        {(group.lead as any)[category.key]?.[param.key] || '-'}
                                                    </td>
                                                )}
                                                {activeTab === 'trail' && group.trail && (
                                                    <td className="px-4 h-12 text-sm text-center border-r border-gray-300 font-mono border-b border-gray-200 align-middle">
                                                        {(group.trail as any)[category.key]?.[param.key] || '-'}
                                                    </td>
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </tr>
                                ))}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
                </div>
            </CardContent>
            </Card>
            </div>
            </>
        );
    };

    return (
        <div className="h-full flex flex-col min-h-0">
            <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'all' | 'lead' | 'trail')} className="flex-1 flex flex-col min-h-0">

                <TabsList className="flex-shrink-0 w-full justify-start gap-4 bg-transparent px-4 py-2 border-none">
                    {/* <TabsTrigger value="all">All Configuration</TabsTrigger> */}
                    <TabsTrigger className="data-[state=active]:border-[#EB1C37] data-[state=active]:text-[#EB1C37] relative px-1 py-2 text-[12px] font-medium text-gray-500 hover:text-gray-700 data-[state=active]:border-b-[0.5px]" value="lead">Lead Torch</TabsTrigger>
                    <TabsTrigger className="data-[state=active]:border-[#EB1C37] data-[state=active]:text-[#EB1C37] relative px-1 py-2 text-[12px] font-medium text-gray-500 hover:text-gray-700 data-[state=active]:border-b-[0.5px]" value="trail">Trail Torch</TabsTrigger>
                </TabsList>

                <TabsContent value="all" className="mt-0 flex-1 min-h-0 overflow-auto data-[state=inactive]:hidden">
                    {renderTableContent()}
                </TabsContent>

                <TabsContent value="lead" className="mt-0 flex-1 min-h-0 overflow-auto data-[state=inactive]:hidden">
                    {renderTableContent()}
                </TabsContent>

                <TabsContent value="trail" className="mt-0 flex-1 min-h-0 overflow-auto data-[state=inactive]:hidden">
                    {renderTableContent()}
                </TabsContent>
            </Tabs>
        </div>
    );
};

export default WeldParametersTable;

